import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../../components';
import { Footer } from '../../containers';
import ContactForm from '../../components/contactform/ContactForm';
import Seo from '../../seo/Seo';
import { SITE } from '../../seo/site.mjs';
import { breadcrumbSchema, webPageSchema } from '../../seo/schemas';
import { MARKETS, DEFAULT_MARKET, servicesByMarket, servicePathFor, servicesIndexPathFor } from '../../data/services.mjs';
import { productsByMarket } from '../../data/products.mjs';
import './services.css';

const HREFLANG = { RD: 'es-DO', LATAM: 'es', USA: 'en-US' };

const COPY = {
    RD: {
        title: 'Servicios de desarrollo de software y marketing digital',
        description:
            'Servicios de Coralan Multiservice en República Dominicana: facturación electrónica e-CF, software a la medida, desarrollo web (Drupal, WordPress, React), apps móviles, eCommerce, automatización con IA, SEO y campañas de Google/Facebook Ads.',
        h1: 'Nuestros servicios',
        subtitle:
            'Desarrollo de software, facturación electrónica, presencia digital y marketing para empresas de República Dominicana y el extranjero. Un solo equipo para diseñar, construir, alojar y hacer crecer su negocio en línea.',
    },
    LATAM: {
        title: 'Servicios de desarrollo de software para empresas de Latinoamérica',
        description:
            'Software a la medida, desarrollo web, aplicaciones móviles, eCommerce, automatización con IA, marketing digital y equipos nearshore desde República Dominicana para empresas de toda Latinoamérica. Precios en USD.',
        h1: 'Servicios para empresas de Latinoamérica',
        subtitle:
            'Equipo de desarrollo en Santo Domingo, en su idioma y su horario, para empresas de toda la región. Cotizaciones en dólares.',
    },
    USA: {
        title: 'Software development services for US companies from the Dominican Republic',
        description:
            'Custom software, web and mobile development, eCommerce, AI automation and nearshore development teams from Santo Domingo, Dominican Republic — same business hours as the US East Coast, English and Spanish.',
        h1: 'Services for US companies',
        subtitle:
            'A nearshore engineering team in Santo Domingo, Dominican Republic, working the same hours as the US East Coast. Pricing in USD.',
        lang: 'en',
    },
};

const ServicesIndexPage = ({ market = DEFAULT_MARKET }) => {
    const copy = COPY[market] || COPY.RD;
    const path = servicesIndexPathFor(market);
    const services = servicesByMarket(market);
    const products = productsByMarket(market);
    const alternates = Object.keys(MARKETS).map((m) => ({ hrefLang: HREFLANG[m], href: servicesIndexPathFor(m) }));
    alternates.push({ hrefLang: 'x-default', href: servicesIndexPathFor(DEFAULT_MARKET) });

    return (
        <div className="service-page">
            <Seo
                title={copy.title}
                description={copy.description}
                path={path}
                lang={copy.lang || 'es'}
                alternates={alternates}
                jsonLd={[
                    webPageSchema({ path, title: copy.title, description: copy.description, type: 'CollectionPage' }),
                    {
                        '@context': 'https://schema.org',
                        '@type': 'ItemList',
                        name: copy.h1,
                        itemListElement: services.map((s, i) => ({
                            '@type': 'ListItem',
                            position: i + 1,
                            name: s.name,
                            url: `${SITE.url}${servicePathFor(s.slug, market)}`,
                        })),
                    },
                    breadcrumbSchema([
                        { name: 'Inicio', path: '/' },
                        { name: 'Servicios', path },
                    ]),
                ]}
            />
            <Navbar />

            <section className="service-page__hero">
                <div className="container">
                    <div className="row">
                        <div className="col-12 col-lg-9 service-page__hero-inner" data-reveal="up">
                            <nav className="service-page__breadcrumb" aria-label="Breadcrumb">
                                <Link to="/">Inicio</Link>
                                <span className="service-page__breadcrumb-sep">/</span>
                                <span>Servicios</span>
                                {market !== DEFAULT_MARKET && (
                                    <>
                                        <span className="service-page__breadcrumb-sep">·</span>
                                        <span>{MARKETS[market].label}</span>
                                    </>
                                )}
                            </nav>
                            <h1 className="service-page__title">{copy.h1}</h1>
                            <p className="service-page__subtitle">{copy.subtitle}</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="service-page__body">
                <div className="container">
                    <div className="row services-index__grid">
                        {services.map((s) => (
                            <div className="col-12 col-md-6 col-lg-4 mb-4" key={s.slug} data-reveal="up">
                                <Link to={servicePathFor(s.slug, market)} className="service-page__related-card lift">
                                    <i className={s.icon} aria-hidden="true"></i>
                                    <h3>{s.name}</h3>
                                    <p>{s.metaDescription}</p>
                                </Link>
                            </div>
                        ))}
                    </div>

                    {products.length > 0 && market === DEFAULT_MARKET && (
                        <div className="service-page__section">
                            <h2 className="service-page__section-title">Nuestros productos</h2>
                            <p className="fables-forth-text-color">
                                Además de servicios a la medida, desarrollamos y mantenemos nuestros propios productos SaaS.{' '}
                                <Link to="/productos">Ver todos los productos →</Link>
                            </p>
                        </div>
                    )}
                </div>
            </section>

            <ContactForm />
            <Footer />
        </div>
    );
};

export default ServicesIndexPage;
